/**
 * report_filter_terms — the JSON a scheduled report stores its filters in, which the
 * report Lambdas read directly and pass to the procs:
 *
 *   { area_filter: { values: "1,2", labels: "Room 1, Room 2" }, …,
 *     time_window: { values: [start, end], labels }, resp_filter_items: { values: [on, op, min], labels },
 *     call_type_filter: { values, labels } }   // Call Logs only
 *
 * One key table and one parse / serialize / merge / summary, replacing five copies in
 * the create drawer, the edit drawer, the schedule drawer, the run helpers and the
 * Scheduled Reports page (docs/DUPLICATION_AUDIT.md, B1). The serialized output is
 * byte-identical to what each of those produced — same keys, same order, same value
 * strings — because the backend parses it; a harness checks that against the old code.
 */

import { expandReasonIds, selectionFromStoredReasonIds } from './reasonFilterIds.js';
import { normalizeTimeWindow, describeTimeWindow } from './reportTimeWindow.js';

/* Order matters: it is the key order of the stored JSON. */
export const FILTER_TERM_KEYS = [
  { stateKey: 'areas', dbKey: 'area_filter', optionsKey: 'areas', idKey: 'area_id', nameKey: 'area_name', label: 'Areas' },
  { stateKey: 'areaGroups', dbKey: 'area_group_filter', optionsKey: 'areaGroups', idKey: 'area_group_id', nameKey: 'area_group_name', label: 'Area Groups' },
  { stateKey: 'areaTypes', dbKey: 'area_type_filter', optionsKey: 'areaTypes', idKey: 'area_type_id', nameKey: 'area_type_name', label: 'Area Types' },
  { stateKey: 'residents', dbKey: 'resident_filter', optionsKey: 'residents', idKey: 'resident_id', nameKey: 'resident_name', label: 'Residents' },
  { stateKey: 'residentGroups', dbKey: 'resident_group_filter', optionsKey: 'residentGroups', idKey: 'resident_group_id', nameKey: 'resident_group_name', label: 'Resident Groups' },
  { stateKey: 'users', dbKey: 'user_filter', optionsKey: 'users', idKey: 'user_id', nameKey: 'display_name', label: 'Users' },
  { stateKey: 'userGroups', dbKey: 'user_group_filter', optionsKey: 'userGroups', idKey: 'group_id', nameKey: 'group_name', label: 'User Groups' },
  { stateKey: 'deviceCategories', dbKey: 'device_category_filter', optionsKey: 'deviceCategories', idKey: 'device_category_id', nameKey: 'device_category_short_name', label: 'Device Categories' },
  { stateKey: 'devices', dbKey: 'device_filter', optionsKey: 'devices', idKey: 'device_id', nameKey: 'device_name', label: 'Devices' },
  { stateKey: 'eventTypes', dbKey: 'event_type_filter', optionsKey: 'eventTypes', idKey: 'event_type_id', nameKey: 'event_type_name', label: 'Event Types' },
  /* Selected by cloud_reason_code_id; stored expanded to cloud + local ids — the same
     set the report pages send (utils/reasonFilterIds.js). Labels stay one per selection. */
  { stateKey: 'reasons', dbKey: 'reason_filter', optionsKey: 'reasonCodes', idKey: 'cloud_reason_code_id', nameKey: 'reason_code_description', label: 'Reasons' },
];

/* Call Logs only: emitted / parsed only when present, so every other report's JSON
   never gains the key. */
const CALL_TYPES = { stateKey: 'callTypes', dbKey: 'call_type_filter', optionsKey: 'callTypes', idKey: 'call_type_id', nameKey: 'call_type_name', label: 'Call Types' };

/* Check-in / Rounds reports only (docs/CHECKIN_WINDOW_FILTER_PLAN.md): window ids, emitted /
   parsed only when present, like call types. `windows` options hold both window types,
   so a stored schedule's chips can name either. */
const WINDOWS = { stateKey: 'windows', dbKey: 'window_filter', optionsKey: 'windows', idKey: 'window_id', nameKey: 'description', label: 'Windows' };

const RESP_OP_LABEL = { lt: 'less than', gt: 'greater than' };

/** The stored value as an object: parses a JSON string, passes an object through, and
 *  returns {} for empty or unparseable input. */
export function readFilterTerms(terms) {
  if (terms == null || terms === '') return {};
  if (typeof terms === 'string') {
    try { return JSON.parse(terms) || {}; } catch { return {}; }
  }
  return typeof terms === 'object' ? terms : {};
}

const splitIds = (v) => (v == null || v === '' ? [] : String(v).split(',').filter(Boolean));

/**
 * Stored terms → page / drawer state: { areas: ['1', '2'], …, responseTime?, callTypes? }.
 * Ids come back as strings, as they are stored. responseTime only when stored with all
 * three values; callTypes only when the key exists (Call Logs); windows likewise
 * (Check-in / Rounds).
 */
export function filterTermsToState(terms) {
  const parsed = readFilterTerms(terms);
  const out = {};
  FILTER_TERM_KEYS.forEach((k) => { out[k.stateKey] = splitIds(parsed[k.dbKey]?.values); });
  const rv = parsed.resp_filter_items?.values;
  if (Array.isArray(rv) && rv.length >= 3) {
    out.responseTime = { enabled: Boolean(rv[0]), operator: rv[1] || 'lt', value: parseInt(rv[2], 10) || 60 };
  }
  if (parsed[CALL_TYPES.dbKey] !== undefined) out.callTypes = splitIds(parsed[CALL_TYPES.dbKey]?.values);
  if (parsed[WINDOWS.dbKey] !== undefined) out.windows = splitIds(parsed[WINDOWS.dbKey]?.values);
  return out;
}

function entry(def, ids, filterOptions, expand) {
  if (ids.length === 0) return { values: '', labels: '' };
  const options = filterOptions?.[def.optionsKey] || [];
  const labels = ids.map((id) => {
    const opt = options.find((o) => String(o[def.idKey]) === String(id));
    return opt ? opt[def.nameKey] : id;
  });
  const values = expand ? expandReasonIds(ids, options) : ids;
  return { values: values.join(','), labels: labels.join(', ') };
}

const toIds = (v) => (v instanceof Set ? [...v] : Array.isArray(v) ? v : []);

/**
 * State → stored terms. Accepts arrays or Sets.
 *
 * @param {object} state — { areas, …, responseTime, timeWindow?, callTypes?, windows? }
 * @param {object} filterOptions — for the labels (and the reason expansion)
 * @param {{ includeTimeWindow?: boolean }} [opts] — the Create drawer writes time_window
 *        (all day as [0, 1440] with an empty label); the Filters editor does not, because
 *        the Schedule drawer owns it and mergeFilterTerms keeps the stored one.
 */
export function stateToFilterTerms(state, filterOptions, { includeTimeWindow = false } = {}) {
  const s = state || {};
  const result = {};
  FILTER_TERM_KEYS.forEach((k) => {
    result[k.dbKey] = entry(k, toIds(s[k.stateKey]), filterOptions, k.stateKey === 'reasons');
  });
  if (includeTimeWindow) {
    const tw = normalizeTimeWindow(s.timeWindow);
    const twLabel = describeTimeWindow(tw.start, tw.end);
    result.time_window = twLabel ? { values: [tw.start, tw.end], labels: twLabel } : { values: [0, 1440], labels: '' };
  }
  const rt = s.responseTime;
  result.resp_filter_items = rt?.enabled
    ? { values: [true, rt.operator, rt.value], labels: `Response Time ${RESP_OP_LABEL[rt.operator] || 'equal to'} ${rt.value} min` }
    : { values: [false, 'lt', 60], labels: '' };
  if (s.callTypes !== undefined) result[CALL_TYPES.dbKey] = entry(CALL_TYPES, toIds(s.callTypes), filterOptions, false);
  if (s.windows !== undefined) result[WINDOWS.dbKey] = entry(WINDOWS, toIds(s.windows), filterOptions, false);
  return result;
}

/**
 * Serialized keys on top of the stored terms. The serializer only emits the keys its
 * editor owns, so replacing the stored terms outright would drop the others — notably
 * time_window, owned by the Schedule drawer — and reset the schedule to all day. Owned
 * keys are always emitted (empty when cleared), so the spread still clears them.
 */
export function mergeFilterTerms(storedTerms, serialized) {
  return { ...readFilterTerms(storedTerms), ...serialized };
}

const nameList = (names) => (names.length > 3 ? `${names.slice(0, 3).join(', ')} +${names.length - 3} more` : names.join(', '));

/**
 * Read-only summary chips for a stored schedule (Schedule drawer "Applied Filters").
 * Names, not a count of values: reasons are stored expanded to cloud + local ids, so
 * counting values could show "Reasons: 4" for two selections.
 *
 * Names come from the live filter options first. The stored labels are only a fallback:
 * the save path strips the backslash from JSON \\u escapes, so any non-ASCII label comes
 * back mangled ("José" → "Josu00e9", the time-window dash → "u2013").
 */
export function filterTermsChips(terms, filterOptions) {
  const parsed = readFilterTerms(terms);
  const chips = [];
  [...FILTER_TERM_KEYS, CALL_TYPES, WINDOWS].forEach((k) => {
    const e = parsed[k.dbKey];
    let ids = splitIds(e?.values);
    if (ids.length === 0) return;
    const options = filterOptions?.[k.optionsKey];
    if (Array.isArray(options) && options.length) {
      if (k.stateKey === 'reasons') ids = selectionFromStoredReasonIds(ids, options);
      const resolved = ids.map((id) => options.find((o) => String(o[k.idKey]) === String(id))?.[k.nameKey]);
      if (resolved.every(Boolean)) {
        chips.push({ key: k.dbKey, label: `${k.label}: ${nameList(resolved)}` });
        return;
      }
    }
    const labels = typeof e.labels === 'string' ? e.labels : '';
    const names = labels ? labels.split(', ') : [];
    /* Labels are joined with ', ', so a name containing ', ' cannot be split back
       reliably. Truncate only when the split lines up with the selection (reasons are
       the exception: their values are expanded, so the split is all there is). */
    const splitTrusted = names.length > 0 && (names.length === ids.length || k.stateKey === 'reasons');
    let text;
    if (splitTrusted) {
      text = nameList(names);
    } else {
      text = labels || String(ids.length);
    }
    chips.push({ key: k.dbKey, label: `${k.label}: ${text}` });
  });
  if (parsed.resp_filter_items?.values?.[0]) {
    chips.push({ key: 'resp', label: parsed.resp_filter_items.labels || 'Response Time' });
  }
  return chips;
}

/** The stored time_window values, or null. */
export function storedTimeWindow(terms) {
  return readFilterTerms(terms)?.time_window?.values ?? null;
}
